/**
 * Optimistic board reorder with rollback — the bank-queue recipe: move
 * locally, persist, invalidate on success, put it back (with a toast) on
 * error. With a campaign group picked, only its rows show and move; they
 * are merged back into their own slots of the plan-wide order.
 * For Media Devoted | Created By Ciro Autiero
 */
import { useEffect, useState } from 'react';
import type { DragEndEvent } from '@dnd-kit/core';
import { arrayMove } from '@dnd-kit/sortable';

import { useToast } from '@/providers/ToastProvider';

import type { DropGenContainerSummary } from '../../drop-generator.types';
import { useDropGeneratorMutations } from '../../useDropGeneratorMutations';
import { mergeSubsetOrder } from './actions/merge-subset-order';

export function useContainerReorder(
  planId: string,
  containers: DropGenContainerSummary[],
  memberIds: ReadonlySet<number> | null,
) {
  const [order, setOrder] = useState(containers);
  useEffect(() => setOrder(containers), [containers]);
  const { reorderContainers, invalidate } = useDropGeneratorMutations();
  const { error } = useToast();
  const visible = memberIds === null ? order : order.filter((c) => memberIds.has(c.campaignWiserId));

  const onDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const ids = visible.map((c) => c.id);
    const from = ids.indexOf(String(active.id));
    const to = ids.indexOf(String(over.id));
    if (from < 0 || to < 0) return;
    const previous = order;
    const next = mergeSubsetOrder(order, arrayMove(visible, from, to));
    setOrder(next);
    reorderContainers.mutate(
      { planId, orderedIds: next.map((c) => c.id) },
      {
        onSuccess: () => invalidate(),
        onError: () => {
          setOrder(previous);
          error('Could not save the order', 'The list was put back as it was.');
        },
      },
    );
  };

  return { order, visible, onDragEnd, saving: reorderContainers.isPending };
}
